
/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
/* eslint-disable no-use-before-define */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// components
import BoxAddressSelected from "../AddressInput/BoxAddressSelected";
import ValidationMessage from "../ValidationMessage/ValidationMessage";

// utils
import useImportScript from "../../../hooks/useImportScript";
import { isObject } from "../../../../domain/utils/utilitarios-frontend-coopeuch/object";
import { extractNumerationFromTyped } from "../../../../domain/utils/utilitarios-frontend-coopeuch/numeration/index";

// styles
import {
  StyledLabel,
  StyledInput,
  StyledWrapperInputAutocomplete,
} from "../AddressInput/styles";

let autoComplete;
let pressKeyDownUp = false;
/* istanbul ignore next */
const URL_GMAPS = process.env.REACT_APP_GMAPS_API;

/* istanbul ignore next */
const EMPTY_ADDRESS = {
  street: "",
  numeration: "",
  commune: "",
  country: "",
  latitude: "",
  longitude: "",
};

/* istanbul ignore next */
const MESSAGES = {
  HELPER_INITIAL:
    "Ingresa tu dirección con calle y número, luego selecciónala de la lista.",
  INVALID_ADDRESS:
    "La dirección ingresada debe tener calle, número, comuna y país",
  NO_COVERAGE:
    "La dirección ingresada no tiene cobertura",
  EDIT_ADDRESS:
    "Edita la dirección y vuelve a seleccionarla de la lista.",
};

/* istanbul ignore next */
const findAddressValueByField = (addressInfo = {}, field = "") => {
  const addressInfoArray = Object.values(addressInfo);
  const addressValues = addressInfoArray.find((addressInfoLitle) =>
    addressInfoLitle.types.some((type) => type === field)
  );
  return addressValues || {};
};

/* istanbul ignore next */
const isObjectWithValues = (obj) =>
  isObject(obj) && Object.values(obj).every((el) => el !== "");

/* istanbul ignore next */
const handleScriptLoad = (
  onSelectAddress,
  onPressEnter,
  autoCompleteRef
) => {
  autoComplete = new window.google.maps.places.Autocomplete(
    autoCompleteRef.current,
    {
      types: ["address"],
      componentRestrictions: { country: "cl" },
      fields: ["geometry", "address_components"],
    }
  );

  google.maps.event.addDomListener(
    autoCompleteRef.current,
    "keydown",
    (e) => {
      if (e.keyCode === 38 || e.keyCode === 40) {
        pressKeyDownUp = true;
      } else if (e.keyCode === 13 && !pressKeyDownUp) {
        onPressEnter();
      }
    }
  );

  autoComplete.addListener("place_changed", () => {
    const addressObject = autoComplete.getPlace() || {};
    let selectedAddress = {};

    if (addressObject.address_components && addressObject.geometry) {
      const street = findAddressValueByField(
        addressObject.address_components,
        "route"
      );
      const numeration = findAddressValueByField(
        addressObject.address_components,
        "street_number"
      );
      const commune = findAddressValueByField(
        addressObject.address_components,
        "administrative_area_level_3"
      );
      const country = findAddressValueByField(
        addressObject.address_components,
        "country"
      );

      const latitude = addressObject.geometry.location.lat();
      const longitude = addressObject.geometry.location.lng();
      const typedValue = autoCompleteRef.current?.value || "";

      const fallbackNumber = extractNumerationFromTyped(
        typedValue,
        street?.long_name || ""
      );

      selectedAddress = {
        street: street.long_name || "",
        numeration: (numeration.long_name || "").trim() || fallbackNumber,
        commune: commune.long_name || "",
        country: country.long_name || "",
        latitude: latitude || "",
        longitude: longitude || "",
      };
    } else {
      const typedValue = autoCompleteRef.current?.value || "";
      selectedAddress = {
        ...EMPTY_ADDRESS,
        numeration: extractNumerationFromTyped(typedValue, ""),
      };
    }

    onSelectAddress(selectedAddress);
  });
};

/* ==========================================================================
  COMPONENT
========================================================================== */

const AddressInput = (props) => {
  const { label, onChange, coverage, onDoSomething, id } = props;
  const { isLoaded: isGmapsLoaded } = useImportScript({ url: URL_GMAPS });

  const [selectedAddress, setSelectedAddress] = useState({});
  const [isValidAddress, setIsValidAddress] = useState(null);
  const [isSelectedOne, setIsSelectedOne] = useState(null);
  const [validationMessage, setValidationMessage] = useState({
    visible: true,
    type: "helper",
    text: MESSAGES.HELPER_INITIAL,
  });

  const autoCompleteRef = useRef(null);

  /* istanbul ignore next */
  useEffect(() => {
    if (isGmapsLoaded) {
      handleScriptLoad(
        (e) => handleSelectAddress(e),
        () => handlePressEnter(),
        autoCompleteRef
      );
    }
  }, [isGmapsLoaded]);

  /* istanbul ignore next */
  useEffect(() => {
    if (Object.keys(selectedAddress).length > 0) {
      onChange({ ...selectedAddress });
    }
  }, [selectedAddress]);

  /* istanbul ignore next */
  useEffect(() => {
    if (coverage && isValidAddress && isSelectedOne) {
      if (
        coverage.value === false ||
        coverage?.code === "7" ||
        id === 2
      ) {
        setValidationMessage({
          visible: true,
          type: "error",
          text: MESSAGES.NO_COVERAGE,
        });
      } else if (coverage.value === true) {
        setValidationMessage({ visible: false, type: "info", text: "" });
      }
    }
  }, [coverage]);

  /* istanbul ignore next */
  const handleSelectAddress = (selectedAddressParam) => {
    if (Object.keys(selectedAddressParam).length) {
      const isValid = isObjectWithValues(selectedAddressParam);

      if (isValid) {
        setValidationMessage({ visible: false, type: "info", text: "" });
        setIsValidAddress(true);
        setIsSelectedOne(true);

        setSelectedAddress({
          ...selectedAddressParam,
        });
      } else {
        setValidationMessage({
          visible: true,
          type: "error",
          text: MESSAGES.INVALID_ADDRESS,
        });
        onDoSomething();
      }
    } else {
      onDoSomething();
    }
  };

  /* istanbul ignore next */
  const handlePressEnter = () => {};

  /* istanbul ignore next */
  const handleOnCloseBoxAddressAndOnBlur = () => {
    pressKeyDownUp = false;

    if (autoCompleteRef.current) {
      autoCompleteRef.current.value = "";
    }

    setSelectedAddress({});
    setIsSelectedOne(false);
    setIsValidAddress(false);
    setValidationMessage({ visible: false, type: "error", text: "" });

    if (typeof onChange === "function") {
      onChange({ ...EMPTY_ADDRESS });
    }
  };

  /* istanbul ignore next */
  const handleOnBlurInputAddress = () => {
    if (!isValidAddress) {
      setValidationMessage({
        visible: true,
        type: "error",
        text: MESSAGES.HELPER_INITIAL,
      });
    }
  };

  /* istanbul ignore next */
  const handleEditAddress = () => {
    pressKeyDownUp = false;

    const valueToEdit = `${selectedAddress.street || ""} ${
      selectedAddress.numeration || ""
    }`.trim();

    if (autoCompleteRef.current) {
      autoCompleteRef.current.value = valueToEdit;
      autoCompleteRef.current.focus();
    }

    setIsValidAddress(false);
    setIsSelectedOne(false);
    setValidationMessage({
      visible: true,
      type: "helper",
      text: MESSAGES.EDIT_ADDRESS,
    });
  };
  /* istanbul ignore next */
  return (
    <div className="search-location-input">
      <StyledLabel>{label}</StyledLabel>

      {isValidAddress && (
        <BoxAddressSelected
          selectedAddress={selectedAddress}
          onClose={handleOnCloseBoxAddressAndOnBlur}
          onEdit={handleEditAddress}
        />
      )}

      <StyledWrapperInputAutocomplete visible={!isValidAddress}>
        <StyledInput
          onBlur={handleOnBlurInputAddress}
          ref={autoCompleteRef}
          placeholder="Ejemplo: Las gardenias 1156 La Florida, Chile"
          autoComplete="new-password"
        />
      </StyledWrapperInputAutocomplete>

      {validationMessage.visible ? (
        <ValidationMessage
          customStyles="margin-top: 4px;"
          type={validationMessage.type}
          message={validationMessage.text}
        />
      ) : (
        <div className="d-none d-md-block pb-1" />
      )}
    </div>
  );
};

AddressInput.defaultProps = {
  label: "",
  id: 0,
  coverage: {
    value: null,
    code: null,
  },
  onChange: () => null,
};

AddressInput.propTypes = {
  label: PropTypes.string,
  coverage: PropTypes.shape({
    value: PropTypes.bool,
    code: PropTypes.any,
  }),
  onChange: PropTypes.func,
  id: PropTypes.number,
};

export default AddressInput;
